import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { UPCOMING_CONSULTATIONS_QUERY_KEY } from '@/hooks/consultations/consultationQueryKeys';
import { getTsPatientPortalClient } from '@montugroup/circuit-shared/api/patient-portal.client';

const client = getTsPatientPortalClient();

/**
 * Leaves the waitlist for a consultation. Any pending offer is passed on to the next patient. Unlike `join`, `leave`
 * rejects when the request fails, so the caller can keep its confirmation open and show an error.
 * @returns `leave`, which takes the consultation code, and whether it's pending
 */
export function useLeaveConsultationWaitlist() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = client.leaveConsultationWaitlist.useMutation({
    retry: false,
    // returning the promise makes mutateAsync wait for the refetch, so callers needn't refetch again; the waitlist
    // only shows on upcoming consultations
    onSuccess: () => queryClient.invalidateQueries({ queryKey: UPCOMING_CONSULTATIONS_QUERY_KEY }),
  });

  const leave = useCallback(
    async (consultationCode: string) => {
      await mutateAsync({ params: { consultationCode }, query: {} });
    },
    [mutateAsync],
  );

  return { leave, isPending };
}
